import { useMemo, useState } from "react";
import { Link } from "react-router";
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useQuery } from "@/lib/useQuery";
import type { CalendarItem } from "@/lib/types";
import { timeOfDay } from "@/lib/format";
import { PageHeader, Loading, Tag } from "@/components/ui";
import { useMe } from "@/app/MeContext";

type View = "week" | "month";
const DAY = 86400000;
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const startOfWeek = (d: Date) => { const s = startOfDay(d); s.setDate(s.getDate() - s.getDay()); return s; }; // Sunday
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * DAY);
const monthLabel = (d: Date) => d.toLocaleDateString("en-US", { month: "long", year: "numeric" });

function visibleRange(view: View, anchor: Date): { start: Date; end: Date } {
  if (view === "week") { const start = startOfWeek(anchor); return { start, end: addDays(start, 7) }; }
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const start = startOfWeek(first);
  return { start, end: addDays(start, 42) };
}

export function CalendarPage() {
  const me = useMe();
  const [view, setView] = useState<View>(() => (window.innerWidth < 760 ? "week" : "month"));
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [showTodos, setShowTodos] = useState(true);
  const { start, end } = useMemo(() => visibleRange(view, anchor), [view, anchor]);
  // Pad a day each side so time zones cannot drop an edge event; the grid filters by local date.
  const from = addDays(start, -1).toISOString();
  const to = addDays(end, 1).toISOString();
  const { data, loading } = useQuery<{ items: CalendarItem[]; warnings: string[] }>(`cal:${from}:${to}:${showTodos}`, () => api.get(`/calendar/range?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&todos=${showTodos ? 1 : 0}`), [from, to, showTodos]);
  const byDay = useMemo(() => {
    const m = new Map<string, CalendarItem[]>();
    for (const it of data?.items ?? []) {
      const k = ymd(new Date(it.startAt));
      (m.get(k) ?? m.set(k, []).get(k)!).push(it);
    }
    for (const list of m.values()) list.sort((a, b) => Number(b.allDay) - Number(a.allDay) || a.startAt.localeCompare(b.startAt));
    return m;
  }, [data]);
  const todayKey = ymd(new Date());
  const step = (n: number) => setAnchor((a) => (view === "week" ? addDays(a, 7 * n) : new Date(a.getFullYear(), a.getMonth() + n, 1)));
  const title = view === "week" ? `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${addDays(end, -1).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : monthLabel(anchor);
  const days = useMemo(() => Array.from({ length: view === "week" ? 7 : 42 }, (_, i) => addDays(start, i)), [start, view]);

  return (
    <>
      <PageHeader title="Calendar">
        <div className="row gap-8 wrap" style={{ marginLeft: "auto" }}>
          <label className="row gap-6 small muted"><input type="checkbox" checked={showTodos} onChange={(e) => setShowTodos(e.target.checked)} /> My todos</label>
          <div className="seg">
            <button className={view === "week" ? "active" : ""} onClick={() => setView("week")}>Week</button>
            <button className={view === "month" ? "active" : ""} onClick={() => setView("month")}>Month</button>
          </div>
          <div className="seg">
            <button onClick={() => step(-1)} title="Previous"><ChevronLeft size={14} /></button>
            <button onClick={() => setAnchor(startOfDay(new Date()))}>Today</button>
            <button onClick={() => step(1)} title="Next"><ChevronRight size={14} /></button>
          </div>
        </div>
      </PageHeader>
      <div className="page">
        <div className="row gap-12 wrap mb-12" style={{ alignItems: "baseline" }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>{title}</h2>
          <span className="small muted">{loading && !data ? "Loading…" : `${data?.items.length ?? 0} items`}</span>
          <span className="row gap-8 small muted" style={{ marginLeft: "auto" }}><span className="cal-key google">Google</span><span className="cal-key opsdesk">From invites</span><span className="cal-key todo">Todo due</span></span>
        </div>
        {me.role === "admin" && data?.warnings.map((w) => <div key={w} className="cal-warn">{w} <Link to="/admin?section=calendar">Admin → Calendar</Link></div>)}
        {loading && !data ? <Loading /> : (
          <div className={`cal-grid ${view}`}>
            {view === "month" && ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="cal-head">{d}</div>)}
            {days.map((d) => {
              const k = ymd(d);
              const items = byDay.get(k) ?? [];
              const other = view === "month" && d.getMonth() !== anchor.getMonth();
              return (
                <div key={k} className={`cal-day ${k === todayKey ? "today" : ""} ${other ? "other" : ""}`} onClick={view === "month" ? () => { setAnchor(d); setView("week"); } : undefined}>
                  <div className="cal-date">{view === "week" ? <>{d.toLocaleDateString("en-US", { weekday: "short" })} <b>{d.getDate()}</b></> : d.getDate()}</div>
                  {items.length === 0 && view === "week" && <div className="xs muted" style={{ padding: "4px 6px" }}>—</div>}
                  {(view === "month" ? items.slice(0, 3) : items).map((it) => <Item key={it.id} it={it} dense={view === "month"} />)}
                  {view === "month" && items.length > 3 && <div className="xs muted" style={{ padding: "0 6px" }}>+{items.length - 3} more</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

function Item({ it, dense }: { it: CalendarItem; dense: boolean }) {
  const href = it.source === "todo" ? "/todos" : it.accountId ? `/accounts/${it.accountId}` : it.partnerId ? `/partners/${it.partnerId}` : null;
  const ext = it.meetingUrl ?? it.url;
  const time = it.allDay ? (it.source === "todo" ? "due" : "all day") : timeOfDay(it.startAt);
  const body = <><span className="cal-time mono">{time}</span><span className={`cal-title truncate ${it.done ? "done" : ""}`}>{it.title}</span></>;
  return (
    <div className={`cal-item ${it.source}`} title={[it.title, it.accountName, it.location, it.organizer].filter(Boolean).join(" · ")} onClick={(e) => e.stopPropagation()}>
      {href ? <Link to={href} className="cal-link">{body}</Link> : ext ? <a href={ext} target="_blank" rel="noreferrer" className="cal-link">{body}</a> : <span className="cal-link">{body}</span>}
      {!dense && (it.accountName || it.location || ext) && (
        <div className="row gap-6 xs muted" style={{ paddingLeft: 6 }}>
          {it.accountName && <span className="truncate">{it.accountName}</span>}
          {it.location && !it.meetingUrl && <span className="truncate">{it.location}</span>}
          {ext && <a href={ext} target="_blank" rel="noreferrer" className="row gap-2" style={{ color: "var(--link)" }}>{it.meetingUrl?.includes("teams") ? "Teams" : it.meetingUrl?.includes("meet.google") ? "Meet" : "Open"} <ExternalLink size={10} /></a>}
          {it.source === "todo" && it.done && <Tag color="green">Done</Tag>}
        </div>
      )}
    </div>
  );
}
